import * as THREE from 'three';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { FeedbackBuffer } from '../feedback-buffer.js';

// Shared-rig contract: forbidden. Ninety-two points of light on black and
// nothing else; a wash would put a floor under them.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'pendula',
  family: 'grid',
  danceability: 0.70,
  energyLevel: 0.66,
  bpmMin: 95,
  bpmMax: 160,
  movement: 0.65,
  tonality: 0.65,
  prefers: { gridStrength: [0.60, 1.0] },
  enabled: true
};

// Pendula: a grid of twelve by eight points of light swinging together,
// half a period a beat, like a hundred tuned pendulums.
//
// ONE GESTURE. Every point follows a pendulum's arc - x = A sin(phase),
// y = -B (1 - cos(phase)) - with the phase advancing by pi a beat, so the
// direction changes ON the beat, exactly, by construction: at a beat edge
// the phase is pi/2 + k pi and the point is at the end of its swing. The
// speed is the derivative of a sine, slow at the ends and fast through the
// centre, which is what makes it swing instead of slide; a 2% overshoot
// right after the turn is the soft snap of the reversal.
//
// ONE VARIATION. uSpread, a phase offset per column from 0 to pi/2: at 0
// the grid is one block; at pi/8 a wave crosses it; at pi/2 a
// checkerboard. It changes on the phrases, eased over four beats, and its
// direction flips on the bars so the wave comes back the other way.

// --- the grid, in frame units (y -1..1, x -aspect..aspect) -------------------
const COLS = 12, ROWS = 8;
const GRID_W = 0.86;                 // of the frame's width the grid spans
const GRID_TOP = 0.72, GRID_BOTTOM = -0.62;
const A_FRAC = 0.40;                 // A = 40% of the column spacing
const B_FRAC = 0.30;                 // the rise at the ends, of A
const OVERSHOOT = 0.02;
const HOLE_COLS = [5, 6], HOLE_ROWS = [3, 4];   // the empty 2 x 2 at the centre
const ANCHOR_Y = 1.6;                // the threads' anchor, above the frame
const PLANE_D = 10;
const SAFE_FLOOR = 0.5;

// --- the render --------------------------------------------------------------
const CORE_PX = 14, HALO_PX = 30;    // at 720 lines
const LUMINANCE = 0.92;
const TRAIL_COPIES = 6;
const TRAIL_MS = 100;
const REFLECT_Y = -0.80;             // the floor line the bottom of the frame mirrors about
const REFLECT_PX = 70;               // the streak's height
const BEAT_PULSE = 0.2;
const SPREADS = [0, Math.PI / 16, Math.PI / 8, Math.PI / 4, Math.PI / 2, Math.PI / 8, 0];
const SPREAD_EASE_BEATS = 4;
const DROP_FREEZE_BEATS = 1;
const DROP_BOOST_BEATS = 4;
const LOW_SWAY = 0.20;               // bands.low on A
const FREE_HALF_PERIOD_S = 0.5;      // no bpm
const PAUSE_SETTLE_S = 1.0;

// --- the background: consequences of the points, nothing else ---------------
// The points are lights: they light a matte wall two units behind the grid.
// Each casts a soft radial glow of 2.5 grid spacings at 0.06 in its colour,
// swinging with it, so the wall breathes at the tempo. The 92 glows are not
// summed in the wall's shader: they are drawn as points into a 64 x 36
// target, blurred by the resolution itself, and the wall samples it
// bilinearly. The wall never passes 0.12 of luminance.
const WALL_BEHIND = 2.0;
const GLOW_W = 64, GLOW_H = 36;
const GLOW_RADIUS_SPACINGS = 2.5;
// LINEAR values, calibrated against the rendered frame (ACES x 1.1 exposure
// x sRGB): a linear 0.12 came out at 97/255 with the whole wall pinned at
// the cap - twenty glows overlap at 2.5 spacings. 0.012 linear is the
// brief's "0.12" on screen (~36/255); a glow is 0.006 with a tight core so
// the wall keeps its pattern under the cap.
const GLOW_INTENSITY = 0.006;
const WALL_LUM_MAX = 0.012;
const SHADOW_OFFSET = 0.045;         // the threads' shadows on the wall, frame units aside
const SHADOW_OPACITY = 0.15;
// A second, identical grid six units behind: 30% the size, 25% the light,
// a quarter beat late. It gives the parallax under the camera's drift and a
// wave in depth; it does not light the wall.
const FAR_BEHIND = 6.0;
const FAR_SIZE = 0.30, FAR_GAIN = 0.25, FAR_LAG = Math.PI / 4;
const CAMERA_DRIFT = 0.14;           // world units, a slow figure

const TIER_LAYOUT = {
  0: { trail: false, reflect: false },
  1: { trail: true, reflect: false },
  2: { trail: true, reflect: true }
};

const _size = new THREE.Vector2();
const _col = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}

// --- shaders -------------------------------------------------------------------

const SAFE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeAt(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// The points: position from the CPU, aCopy 0 for the head and 1..6 for the
// trail, aRow for the colour, aPulse the beat's pulse. uMirror = 1 draws
// the reflection: a soft vertical streak below the floor line.
const pointVertexShader = `
  precision mediump float;
  attribute float aRow;
  attribute float aCopy;
  uniform float uPx;           // device pixels per 720-line pixel
  uniform float uMirror;
  uniform float uPulse;
  uniform float uRowShift;
  uniform float uSizeMul;
  uniform float uGainMul;
  uniform vec3 uRowCol[${ROWS}];
  varying vec3 vCol;
  varying float vCopy;
  varying float vGain;
  ${SAFE_CHUNK}
  void main() {
    int r = int(mod(aRow + uRowShift, ${ROWS}.0));
    vec3 c = uRowCol[0];
    for (int i = 1; i < ${ROWS}; i++) if (i == r) c = uRowCol[i];
    vCol = c;
    vCopy = aCopy;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vec4 clip = projectionMatrix * mv;
    gl_Position = clip;
    float trail = 1.0 - aCopy / ${(TRAIL_COPIES + 1).toFixed(1)};
    gl_PointSize = (uMirror > 0.5 ? ${REFLECT_PX.toFixed(1)} : ${HALO_PX.toFixed(1)} * (0.6 + 0.4 * trail)) * uPx * uSizeMul;
    vGain = safeAt(clip) * (aCopy < 0.5 ? 1.0 + uPulse : 0.45 * trail * trail) * (uMirror > 0.5 ? 0.30 : 1.0) * uGainMul;
  }
`;
const pointFragmentShader = `
  precision mediump float;
  uniform float uMirror;
  varying vec3 vCol;
  varying float vCopy;
  varying float vGain;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    vec3 col;
    if (uMirror > 0.5) {
      // The reflection: a streak, tight across, fading down.
      float across = exp(-d.x * d.x * 90.0);
      float down = (1.0 - smoothstep(-0.5, 0.5, d.y)) * smoothstep(-0.55, -0.3, d.y);
      col = vCol * across * down * 0.7;
    } else {
      float r = length(d) * 2.0;
      float core = 1.0 - smoothstep(0.0, ${(CORE_PX / HALO_PX).toFixed(3)}, r);
      // The halo dies before the sprite's edge: ninety-two overlapping
      // halos under the bloom washed the black to a teal fog.
      float halo = exp(-r * r * 5.0) * (1.0 - core * 0.5) * (1.0 - smoothstep(0.7, 1.0, r));
      col = (vec3(1.0) * core + vCol * halo * 0.6) * ${LUMINANCE.toFixed(2)};
      if (vCopy > 0.5) col = vCol * (halo * 0.8 + core * 0.4) * ${LUMINANCE.toFixed(2)};
    }
    gl_FragColor = vec4(col * vGain, 1.0);
  }
`;

// The wall: matte, grainy, a few shades of tint and no pattern, lit by the
// glow texture.
const wallVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
const wallFragmentShader = `
  precision mediump float;
  uniform sampler2D uGlow;
  uniform vec2 uGlowUv;        // frame -> glow uv scale (the wall overruns the frame)
  varying vec2 vUv;
  float hash21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
  void main() {
    vec2 f = (vUv - 0.5) * uGlowUv + 0.5;
    vec3 glow = texture2D(uGlow, f).rgb;
    float grain = 0.85 + 0.3 * hash21(floor(vUv * vec2(640.0, 360.0)));
    vec3 tint = vec3(0.0025, 0.0024, 0.0028) + vec3(0.0006, 0.0003, -0.0004) * sin(vUv.x * 5.0 + vUv.y * 3.0);
    vec3 col = tint * grain + glow * (0.9 + 0.2 * grain);
    float lum = dot(col, vec3(0.299, 0.587, 0.114));
    if (lum > ${WALL_LUM_MAX.toFixed(2)}) col *= ${WALL_LUM_MAX.toFixed(2)} / lum;
    gl_FragColor = vec4(col, 1.0);
  }
`;
// The glows, drawn into the small target: one point per head in clip space.
const glowVertexShader = `
  precision mediump float;
  attribute float aRow;
  uniform float uSizeTexels;
  uniform float uRowShift;
  uniform vec3 uRowCol[${ROWS}];
  varying vec3 vCol;
  void main() {
    int r = int(mod(aRow + uRowShift, ${ROWS}.0));
    vec3 c = uRowCol[0];
    for (int i = 1; i < ${ROWS}; i++) if (i == r) c = uRowCol[i];
    vCol = c;
    gl_Position = vec4(position.xy, 0.0, 1.0);
    gl_PointSize = uSizeTexels;
  }
`;
const glowFragmentShader = `
  precision mediump float;
  varying vec3 vCol;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float g = exp(-dot(d, d) * 22.0) * (1.0 - smoothstep(0.35, 0.5, length(d)));
    gl_FragColor = vec4(vCol * g * ${GLOW_INTENSITY.toFixed(3)}, 1.0);
  }
`;

export class PendulaVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER_LAYOUT[this._tier] || TIER_LAYOUT[1];
    const sp = parseFloat(urlParam('pendspread'));
    this._forceSpread = Number.isFinite(sp) ? sp : null;
    this._debugDrop = urlParam('penddrop') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._placeCamera();
    this._halfH = PLANE_D * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    this._aspect = camera.aspect || 16 / 9;
    this._time = 0;
    this._beatSeconds = 0.5;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrase = null;
    this._freeBeatAt = 0; this._beatCount = 0; this._beats = 0;
    this._pendingDropMs = null; this._lastMode = null; this._lastDropAt = -1e9;
    this._spread = 0; this._spreadFrom = 0; this._spreadTo = 0; this._spreadAt = -1e9; this._spreadIdx = 0;
    this._spreadDir = 1;
    this._rowShift = 0;
    this._freezeUntil = -1e9; this._boostUntil = -1e9; this._phaseOffset = 0;
    this._pauseBlend = 0; this._pausedPhase = 0;
    this._debugDropAt = 3;
    this._objects = []; this._materials = []; this._geometries = [];
    this._buildGrid();
    this._buildWall();
    this._buildPoints();
    this._buildThreads();
    this._applyPalette(null);
  }

  // A slow figure, a few centimetres: the far grid's parallax comes from it.
  _placeCamera() {
    const c = this._camera, t = this._time || 0;
    c.position.set(Math.sin(t * 0.21) * CAMERA_DRIFT, Math.sin(t * 0.13) * CAMERA_DRIFT * 0.5, 0);
    c.lookAt(c.position.x * 0.3, c.position.y * 0.3, -PLANE_D);
    c.updateMatrixWorld();
  }

  _buildWall() {
    const zw = -(PLANE_D + WALL_BEHIND);
    const hh = -zw * Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2) * 1.25;
    const geo = new THREE.PlaneGeometry(hh * 2 * this._aspect, hh * 2);
    this._glow = new FeedbackBuffer({ width: GLOW_W, height: GLOW_H, single: true });
    this._wallUniforms = { uGlow: { value: null }, uGlowUv: { value: new THREE.Vector2(1.25, 1.25) } };
    const mat = new THREE.ShaderMaterial({ uniforms: this._wallUniforms, vertexShader: wallVertexShader, fragmentShader: wallFragmentShader, depthTest: false, depthWrite: false });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(0, 0, zw);
    mesh.frustumCulled = false; mesh.renderOrder = -1000;
    this._objects.push(mesh); this._geometries.push(geo); this._materials.push(mat);
    this._scene.add(mesh);
    // The glow scene: the heads as points in clip space.
    const n = this._cells.length;
    const pos = new Float32Array(n * 3), row = new Float32Array(n);
    for (let i = 0; i < n; i++) row[i] = this._cells[i].row;
    const ggeo = new THREE.BufferGeometry();
    ggeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    ggeo.setAttribute('aRow', new THREE.BufferAttribute(row, 1));
    const rowCol = [];
    for (let r = 0; r < ROWS; r++) rowCol.push(new THREE.Color(1, 1, 1));
    // The radius in texels: 2.5 spacings of the frame's width, over 64 texels.
    const sizeTexels = 2 * GLOW_RADIUS_SPACINGS * this._sx / (2 * this._aspect) * GLOW_W;
    this._glowUniforms = { uSizeTexels: { value: sizeTexels }, uRowShift: { value: 0 }, uRowCol: { value: rowCol } };
    const gmat = new THREE.ShaderMaterial({ uniforms: this._glowUniforms, vertexShader: glowVertexShader, fragmentShader: glowFragmentShader, transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false });
    const gpts = new THREE.Points(ggeo, gmat);
    gpts.frustumCulled = false;
    this._glowScene = new THREE.Scene();
    this._glowScene.add(gpts);
    this._glowPts = { pts: gpts, pos, geo: ggeo };
    this._geometries.push(ggeo); this._materials.push(gmat);
    this._glowCamera = new THREE.Camera();
  }

  // The glow target, rendered from update(), never nested in a render.
  _renderGlow() {
    const renderer = this._renderer;
    if (!renderer) return;
    this._glow.ensureSize(renderer);
    const prev = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    renderer.setRenderTarget(this._glow.write);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, false, false);
    renderer.autoClear = false;
    renderer.render(this._glowScene, this._glowCamera);
    renderer.autoClear = prevAutoClear;
    renderer.setRenderTarget(prev);
    this._wallUniforms.uGlow.value = this._glow.write.texture;
  }

  _buildGrid() {
    this._cells = [];
    const A = this._aspect;
    const w = 2 * A * GRID_W;
    this._sx = w / (COLS - 1);
    this._sy = (GRID_TOP - GRID_BOTTOM) / (ROWS - 1);
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (HOLE_COLS.includes(c) && HOLE_ROWS.includes(r)) continue;
        this._cells.push({ col: c, row: r, cx: -w / 2 + c * this._sx, cy: GRID_TOP - r * this._sy, x: 0, y: 0 });
      }
    }
    this._A = this._sx * A_FRAC;
  }

  _points(copies, mirror, order) {
    const n = this._cells.length * copies;
    const pos = new Float32Array(n * 3), row = new Float32Array(n), copy = new Float32Array(n);
    let i = 0;
    for (const cell of this._cells) for (let k = 0; k < copies; k++) { row[i] = cell.row; copy[i] = k; i++; }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('aRow', new THREE.BufferAttribute(row, 1));
    geo.setAttribute('aCopy', new THREE.BufferAttribute(copy, 1));
    const rowCol = [];
    for (let r = 0; r < ROWS; r++) rowCol.push(new THREE.Color(1, 1, 1));
    const u = {
      uPx: { value: 1 }, uMirror: { value: mirror ? 1 : 0 }, uPulse: { value: 0 }, uRowShift: { value: 0 }, uRowCol: { value: rowCol }, uSizeMul: { value: 1 }, uGainMul: { value: 1 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    const mat = new THREE.ShaderMaterial({ uniforms: u, vertexShader: pointVertexShader, fragmentShader: pointFragmentShader, transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false });
    const pts = new THREE.Points(geo, mat);
    pts.frustumCulled = false;
    pts.renderOrder = order;
    this._objects.push(pts); this._geometries.push(geo); this._materials.push(mat);
    this._scene.add(pts);
    return { pts, geo, pos, uniforms: u, copies };
  }

  _buildPoints() {
    const copies = this._layout.trail ? TRAIL_COPIES + 1 : 1;
    this._heads = this._points(copies, false, -500);
    this._mirror = this._layout.reflect ? this._points(1, true, -600) : null;
    this._far = this._points(1, false, -800);
    this._far.uniforms.uSizeMul = { value: FAR_SIZE };
    this._far.uniforms.uGainMul = { value: FAR_GAIN };
    const self = this;
    this._heads.pts.onBeforeRender = function (renderer) {
      self._renderer = renderer;
      renderer.getDrawingBufferSize(_size);
      const px = Math.max(0.5, _size.y / 720);
      self._heads.uniforms.uPx.value = px;
      if (self._mirror) self._mirror.uniforms.uPx.value = px;
      self._far.uniforms.uPx.value = px;
      self._threadMat.resolution.set(_size.x, _size.y);
      self._shadowMat.resolution.set(_size.x, _size.y);
    };
  }

  _buildThreads() {
    const n = this._cells.length;
    const geo = new LineSegmentsGeometry();
    this._threadPos = new Float32Array(n * 6);
    const col = new Float32Array(n * 6);
    geo.setPositions(this._threadPos); geo.setColors(col);
    this._threadCol = col;
    const mat = new LineMaterial({ linewidth: 1.0, vertexColors: true, transparent: true, opacity: 0.5, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false, dashed: true, dashSize: 1e9, gapSize: 0, alphaToCoverage: false });
    mat.resolution.set(1280, 720);
    this._threadMat = mat;
    const mesh = new LineSegments2(geo, mat);
    mesh.frustumCulled = false; mesh.renderOrder = -700;
    this._threads = { mesh, geo, posBuf: geo.attributes.instanceStart.data, colBuf: geo.attributes.instanceColorStart.data };
    this._objects.push(mesh); this._geometries.push(geo); this._materials.push(mat);
    this._scene.add(mesh);
    // The threads' shadows on the wall: a dark line a little aside, on the
    // wall's plane, blended normally so it darkens.
    const sgeo = new LineSegmentsGeometry();
    this._shadowPos = new Float32Array(n * 6);
    sgeo.setPositions(this._shadowPos);
    const smat = new LineMaterial({ linewidth: 1.0, color: 0x000000, transparent: true, opacity: SHADOW_OPACITY, depthTest: false, depthWrite: false, dashed: true, dashSize: 1e9, gapSize: 0, alphaToCoverage: false });
    smat.resolution.set(1280, 720);
    this._shadowMat = smat;
    const smesh = new LineSegments2(sgeo, smat);
    smesh.frustumCulled = false; smesh.renderOrder = -900;
    this._shadows = { mesh: smesh, geo: sgeo, posBuf: sgeo.attributes.instanceStart.data };
    this._objects.push(smesh); this._geometries.push(sgeo); this._materials.push(smat);
    this._scene.add(smesh);
  }

  _applyPalette(profile) {
    const palette = profile && profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const src = vivid.length >= 2 ? vivid : (vivid.length + dominant.length >= 2 ? vivid.concat(dominant) : ['#3fb6ff', '#ff4fa0']);
    const top = new THREE.Color(src[0]), bottom = new THREE.Color(src[1 % src.length]);
    // A gradient of the palette from the top row to the bottom, kept vivid.
    for (const set of [this._heads, this._mirror, this._far, this._glowPts && { uniforms: this._glowUniforms }]) {
      if (!set) continue;
      for (let r = 0; r < ROWS; r++) {
        _col.copy(top).lerp(bottom, r / (ROWS - 1));
        _col.getHSL(_hsl);
        _col.setHSL(_hsl.h, Math.max(0.6, _hsl.s), THREE.MathUtils.clamp(_hsl.l, 0.5, 0.65));
        set.uniforms.uRowCol.value[r].copy(_col);
      }
    }
  }

  onTrackChange(profile) { this._applyPalette(profile); }

  update(deltaTime, profile) {
    this._placeCamera();
    const director = profile.director || {};
    const paused = profile.playbackState !== 'PLAYING';
    if (profile.artworkPalette && profile.artworkPalette !== this._lastPalette) { this._lastPalette = profile.artworkPalette; this._applyPalette(profile); }
    const bpmRaw = profile.trackFeatures && profile.trackFeatures.bpm;
    const bpm = bpmRaw > 0 ? THREE.MathUtils.clamp(bpmRaw, 40, 200) : 0;
    this._beatSeconds = bpm ? 60 / bpm : FREE_HALF_PERIOD_S;
    const beat = this._beatSeconds;
    const measured = !!director.hasReliableGrid;
    const bands = director.bands || { low: 0, high: 0 };
    const low = measured ? (bands.low || 0) : 0.5, high = measured ? (bands.high || 0) : 0.5;
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    const t = this._time;

    if (!paused) {
      // --- the clocks: a continuous beat count, and the edges
      let beatEdge = false, barEdge = false, phraseEdge = false;
      if (profile.beatPhase != null) {
        if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
        this._lastBeatPhase = profile.beatPhase;
      } else if (t - this._freeBeatAt >= beat) { this._freeBeatAt = t; beatEdge = true; }
      if (beatEdge) this._beatCount++;
      const beatPhase = profile.beatPhase != null ? profile.beatPhase : Math.min(1, (t - this._freeBeatAt) / beat);
      this._beats = this._beatCount + beatPhase;
      this._beatPhase = beatPhase;
      if (profile.barPhase != null) {
        if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) barEdge = true;
        this._lastBarPhase = profile.barPhase;
      } else if (beatEdge && this._beatCount % 4 === 0) barEdge = true;
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
      if (phrase == null && beatEdge && this._beatCount % 16 === 0) phraseEdge = true;
      this._lastPhrase = phrase;

      // --- the bar: the spread's direction flips, the gradient steps a row
      if (barEdge) { this._spreadDir = -this._spreadDir; this._rowShift = (this._rowShift + 1) % ROWS; }
      // --- the phrase: a new spread, eased over four beats
      if (phraseEdge) {
        this._spreadIdx = (this._spreadIdx + 1 + Math.floor(Math.random() * (SPREADS.length - 1))) % SPREADS.length;
        this._spreadFrom = this._spread; this._spreadTo = SPREADS[this._spreadIdx]; this._spreadAt = this._beats;
      }
      const su = THREE.MathUtils.clamp((this._beats - this._spreadAt) / SPREAD_EASE_BEATS, 0, 1);
      this._spread = this._spreadFrom + (this._spreadTo - this._spreadFrom) * su * su * (3 - 2 * su);
      if (this._forceSpread != null) this._spread = this._forceSpread;

      // --- the drop: everything stops at the centre for a beat, then leaves
      // together with A doubled for a bar
      const ev = director.nextEvent;
      let dropNow = false;
      if (ev && ev.type === 'drop' && ev.msUntil != null) {
        if (ev.msUntil <= dt * 500 + 30) dropNow = true;
        if (this._pendingDropMs != null && this._pendingDropMs < 400 && ev.msUntil > this._pendingDropMs + 250) dropNow = true;
        this._pendingDropMs = ev.msUntil;
      } else {
        if (this._pendingDropMs != null && this._pendingDropMs < 400) dropNow = true;
        this._pendingDropMs = null;
      }
      if (measured && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
      this._lastMode = director.mode;
      if (this._debugDrop && t >= this._debugDropAt) { dropNow = true; this._debugDropAt = t + 8 * beat; }
      if (dropNow && t - this._lastDropAt > 2 * beat) {
        this._lastDropAt = t;
        this._freezeUntil = this._beats + DROP_FREEZE_BEATS;
        this._boostUntil = this._beats + DROP_FREEZE_BEATS + DROP_BOOST_BEATS;
      }
      // Resuming: the points leave from the centre, so the phase carries an
      // offset of a quarter period that is paid back over the boosted bar -
      // after which the turns are on the beat again.
      const frozen = this._beats < this._freezeUntil;
      if (frozen) this._phaseOffset = Math.PI / 2;
      else if (this._phaseOffset > 0) this._phaseOffset = Math.max(0, this._phaseOffset - dt / (DROP_BOOST_BEATS * beat) * (Math.PI / 2));
      this._frozen = frozen;
      this._boost = this._beats < this._boostUntil && !frozen ? 2 : 1;
      this._pauseBlend = Math.max(0, this._pauseBlend - dt / PAUSE_SETTLE_S);
    } else {
      // Paused: the points settle at the centre in a second, like pendulums.
      if (this._pauseBlend === 0) this._pausedPhase = this._livePhase();
      this._pauseBlend = Math.min(1, this._pauseBlend + dt / PAUSE_SETTLE_S);
    }

    this._writeFrame(low, high, paused);
  }

  _livePhase() { return Math.PI * this._beats + Math.PI / 2 - this._phaseOffset; }

  // The pendulum, for one column, at a phase delay (0 for the head, more
  // for the trail): x, y in frame units relative to the cell.
  _pendulum(phase, s, A, out) {
    // The overshoot: just after a turn (s = 0 at the beat) the swing goes 2%
    // past the end and comes back.
    const over = 1 + OVERSHOOT * Math.exp(-s * 7) * Math.sin(s * 14);
    out.x = A * Math.sin(phase) * over;
    // The rise: highest at the ends of the swing, lowest through the centre.
    // (The brief's -B (1 - cos) assumes a phase counted from the centre over
    // a half period; over a running phase it is sin^2, the same arc.)
    out.y = A * B_FRAC * Math.sin(phase) * Math.sin(phase);
  }

  _writeFrame(low, high, paused) {
    const H = this._halfH;
    const A = this._A * (1 + LOW_SWAY * (low * 2 - 1)) * (this._boost || 1);
    let phase0 = this._livePhase();
    const s = this._beatPhase || 0;
    if (this._frozen || paused) {
      // At the centre: the nearest multiple of pi.
      const target = Math.round(this._pausedPhase != null && paused ? this._pausedPhase / Math.PI : phase0 / Math.PI) * Math.PI;
      const blend = paused ? this._pauseBlend : 1;
      phase0 = (paused ? this._pausedPhase : phase0) * (1 - blend) + target * blend;
    }
    // The trail: copies at increasing delays over the last 100 ms, longer
    // with the highs.
    const trailPhase = (TRAIL_MS / 1000 / this._beatSeconds) * Math.PI * (0.6 + 0.8 * high);
    const heads = this._heads, mirror = this._mirror, far = this._far;
    const copies = heads.copies;
    const P = heads.pos, M = mirror ? mirror.pos : null, F = far.pos, G = this._glowPts.pos, SP = this._shadowPos;
    const TP = this._threadPos, TC = this._threadCol;
    const A_ = this._aspect;
    const zWall = -(PLANE_D + WALL_BEHIND), zFar = -(PLANE_D + FAR_BEHIND);
    const wallScale = -zWall / PLANE_D, farScale = -zFar / PLANE_D;
    const o = { x: 0, y: 0 };
    let i = 0;
    for (let c = 0; c < this._cells.length; c++) {
      const cell = this._cells[c];
      const colPhase = this._spread * this._spreadDir * (cell.col - (COLS - 1) / 2);
      for (let k = 0; k < copies; k++) {
        const ph = phase0 + colPhase - (this._frozen || paused ? 0 : k * trailPhase / TRAIL_COPIES);
        this._pendulum(ph, s, A, o);
        const x = (cell.cx + o.x) * H, y = (cell.cy + o.y) * H;
        P[i * 3] = x; P[i * 3 + 1] = y; P[i * 3 + 2] = -PLANE_D;
        if (k === 0) {
          // The thread, from the point up to its anchor above the frame.
          TP[c * 6] = x; TP[c * 6 + 1] = y; TP[c * 6 + 2] = -PLANE_D;
          TP[c * 6 + 3] = cell.cx * H; TP[c * 6 + 4] = ANCHOR_Y * H; TP[c * 6 + 5] = -PLANE_D;
          const g = 0.18;
          TC[c * 6] = g; TC[c * 6 + 1] = g; TC[c * 6 + 2] = g; TC[c * 6 + 3] = 0; TC[c * 6 + 4] = 0; TC[c * 6 + 5] = 0;
          if (M) { M[c * 3] = x; M[c * 3 + 1] = (2 * REFLECT_Y - (cell.cy + o.y)) * H - REFLECT_PX / 720 * H * 0.5; M[c * 3 + 2] = -PLANE_D; }
          // The glow behind the point, in the small target's clip space.
          G[c * 3] = (cell.cx + o.x) / A_; G[c * 3 + 1] = cell.cy + o.y; G[c * 3 + 2] = 0;
          // The thread's shadow: on the wall, a little aside, same swing.
          SP[c * 6] = (cell.cx + o.x + SHADOW_OFFSET) * H * wallScale; SP[c * 6 + 1] = y * wallScale; SP[c * 6 + 2] = zWall;
          SP[c * 6 + 3] = (cell.cx + SHADOW_OFFSET) * H * wallScale; SP[c * 6 + 4] = ANCHOR_Y * H * wallScale; SP[c * 6 + 5] = zWall;
          // The far grid: the same swing a quarter beat late, six units back.
          this._pendulum(ph - FAR_LAG, s, A, o);
          F[c * 3] = (cell.cx + o.x) * H * farScale; F[c * 3 + 1] = (cell.cy + o.y) * H * farScale; F[c * 3 + 2] = zFar;
        }
        i++;
      }
    }
    far.geo.attributes.position.needsUpdate = true;
    this._glowPts.geo.attributes.position.needsUpdate = true;
    this._shadows.posBuf.needsUpdate = true;
    this._glowUniforms.uRowShift.value = this._rowShift;
    this._renderGlow();
    heads.geo.attributes.position.needsUpdate = true;
    if (mirror) mirror.geo.attributes.position.needsUpdate = true;
    this._threads.posBuf.needsUpdate = true; this._threads.colBuf.needsUpdate = true;
    // The beat's pulse at the end of the swing, and the gradient's step.
    const pulse = (this._frozen || paused) ? 0 : BEAT_PULSE * Math.exp(-s * 9);
    heads.uniforms.uPulse.value = pulse; heads.uniforms.uRowShift.value = this._rowShift;
    if (mirror) { mirror.uniforms.uPulse.value = pulse; mirror.uniforms.uRowShift.value = this._rowShift; }
    far.uniforms.uPulse.value = pulse; far.uniforms.uRowShift.value = this._rowShift;
  }

  dispose() {
    if (this._glow) { this._glow.dispose(); this._glow = null; }
    for (const o of this._objects) if (o.parent) o.parent.remove(o);
    for (const g of this._geometries) g.dispose();
    for (const m of this._materials) m.dispose();
    this._objects = []; this._geometries = []; this._materials = [];
    this._scene = null;
  }
}
